import { Flex, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IncomingCallTone from '~/assets/sounds/incoming-call-waiting-ringtone.mp3'
import { useExperimentsState } from '~/atoms/ExperimentsState'
import { Ringtone, useSettingsState } from '~/atoms/SettingsState'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import { MangoModal } from '~/components/shared/MangoModal'
import { useAnalytics } from '~/hooks/useAnalytics'
import useElectronComms from '~/hooks/useElectronComms'
import { useMainWindow, usePopupWindow } from '~/hooks/useElectronWindow'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { Size } from '~/types/ThemeTypes'
import { noop } from '~/utils/functions'

import { toMaskedPhoneNumber } from '@mangovoice/core'
import { CallMetadata, PhoneCallStatus } from '@mangovoice/types'

import { IncomingCallButtonVariants } from './componentStyles/IncomingCallButton.style'
import {
  IncomingCallModalVariants,
  useIncomingCallModalStyles,
} from './componentStyles/IncomingCallModal.style'
import { IncomingCallButton, ParkCallButton } from './IncomingCallButton'

export const IncomingCallModal = () => {
  const { focusMainWindow } = useMainWindow()
  const { addCallToPopupQueue, clearCallsFromPopup } = usePopupWindow()
  const { t } = useTranslation()
  const {
    experimentState: { callPopEnabled },
  } = useExperimentsState()

  const [, setCallMetadata] = useSharedVariable<CallMetadata>('CallMetadata')
  const [showCallModal] = useSharedVariable<boolean>('ShowCallModal', true)
  const [, setParkingLotShared] =
    useSharedVariable<TrackedParkingLot[]>('ParkingLots')

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        invite: { pendingInvitation },
        isAtMaxCallLimit,
      },
      trackedParkingLots: parkingLots,
    },
    actions: {
      answerCall,
      answerCallWithHoldCurrent,
      answerCallWithEndCurrent,
      answerCallWithParkCurrent,
      rejectCall,
      rejectCallWithBusy,
    },
  } = usePhoneEndpoint({})

  const { initHandlePhoneCallListener } = useElectronComms()

  const {
    settings: {
      webPhone: { ringtone, doNotDisturbTimestamp, hasCallWaiting },
    },
  } = useSettingsState()
  const doNotDisturb: boolean = doNotDisturbTimestamp !== undefined

  const [shouldRender, setShouldRender] = useState(false)

  const handleRejectButtonClick = () => {
    rejectCall()
    track(WebPhoneAnalyticsEvents.InviteRejectedClick)
  }

  const handleParkCallButtonClick = (parkingLot: TrackedParkingLot) => {
    answerCallWithParkCurrent(parkingLot.targetNumber)
  }

  const shouldRing = ringtone !== Ringtone.SILENT

  const hasNoActiveCall = !activeCall && !isAtMaxCallLimit
  const mustRejectOrEndCall = activeCall !== undefined

  const canParkOrHoldCall =
    activeCall &&
    activeCall.status === PhoneCallStatus.CONNECTED &&
    !isAtMaxCallLimit
  const variant = canParkOrHoldCall
    ? IncomingCallModalVariants.DoubleRow
    : IncomingCallModalVariants.SingleRow
  const styles = useIncomingCallModalStyles({ variant })

  useEffect(() => {
    setCallMetadata({
      hasNoActiveCall,
      mustRejectOrEndCall,
      canParkOrHoldCall: canParkOrHoldCall || false,
    })
  }, [hasNoActiveCall, mustRejectOrEndCall, canParkOrHoldCall])

  useEffect(() => {
    const removeListener = initHandlePhoneCallListener({
      answerCall,
      rejectCall: handleRejectButtonClick,
      answerCallWithEnd: answerCallWithEndCurrent,
      answerCallWithHold: answerCallWithHoldCurrent,
      parkCall: handleParkCallButtonClick,
      parkingLots,
    })

    return () => {
      removeListener()
    }
  }, [
    answerCall,
    rejectCall,
    answerCallWithEndCurrent,
    answerCallWithHoldCurrent,
    handleParkCallButtonClick,
    parkingLots,
  ])

  useEffect(() => {
    setParkingLotShared(parkingLots)
  }, [parkingLots])

  useEffect(() => {
    if (doNotDisturb && pendingInvitation) {
      rejectCall()
      track(WebPhoneAnalyticsEvents.InviteRejectedDND)
      setShouldRender(false)
      return
    }

    console.log('2nd call status:', {
      hasCallWaiting,
      activeCall,
      pendingInvitation,
    })

    if (hasCallWaiting && activeCall && pendingInvitation) {
      console.log('Rejecting call with busy')
      rejectCallWithBusy()
      track(WebPhoneAnalyticsEvents.InviteRejectedBusy)
      setShouldRender(false)
      return
    }

    setShouldRender(!!pendingInvitation)

    if (
      pendingInvitation &&
      ((hasCallWaiting && !activeCall) || !hasCallWaiting)
    ) {
      if (callPopEnabled) {
        addCallToPopupQueue(pendingInvitation)
      } else {
        focusMainWindow()
      }
    } else {
      if (callPopEnabled) {
        clearCallsFromPopup()
      }
    }
  }, [
    pendingInvitation,
    doNotDisturb,
    hasCallWaiting,
    activeCall,
    callPopEnabled,
  ])

  if (!shouldRender || !pendingInvitation || !showCallModal) {
    return null
  }

  return (
    <MangoModal
      isOpen
      onClose={noop}
      closeButton={false}
      headerDisabled={true}
      size={Size.md}
    >
      <Flex sx={styles.modal}>
        <Flex sx={styles.headerBlock}>
          <Text sx={styles.headerText}>
            {t('incomingCallModal.newCallFrom')}
          </Text>
          <Text sx={styles.callerName}>{pendingInvitation.name}</Text>
          <Text sx={styles.callerNumber}>
            {toMaskedPhoneNumber(pendingInvitation.number)}
          </Text>
          <audio
            id="incoming-call-tone"
            src={IncomingCallTone}
            autoPlay={shouldRing}
            loop
          />
        </Flex>

        {hasNoActiveCall && (
          <Flex sx={styles.buttonRow}>
            <IncomingCallButton
              variant={IncomingCallButtonVariants.Reject}
              onClick={handleRejectButtonClick}
            >
              {t('incomingCallModal.rejectCall')}
            </IncomingCallButton>

            <IncomingCallButton
              variant={IncomingCallButtonVariants.Accept}
              onClick={answerCall}
            >
              {t('incomingCallModal.acceptCall')}
            </IncomingCallButton>
          </Flex>
        )}
        {mustRejectOrEndCall && (
          <Flex sx={styles.buttonRow}>
            <IncomingCallButton
              variant={IncomingCallButtonVariants.Reject}
              onClick={handleRejectButtonClick}
            >
              {t('incomingCallModal.rejectCall')}
            </IncomingCallButton>

            <IncomingCallButton
              variant={IncomingCallButtonVariants.EndAndAccept}
              onClick={answerCallWithEndCurrent}
            >
              {t('incomingCallModal.endAndAccept')}
            </IncomingCallButton>
          </Flex>
        )}
        {canParkOrHoldCall && (
          <Flex sx={styles.buttonRow}>
            <IncomingCallButton
              variant={IncomingCallButtonVariants.HoldAndAccept}
              onClick={answerCallWithHoldCurrent}
            >
              {t('incomingCallModal.holdAndAccept')}
            </IncomingCallButton>

            <ParkCallButton
              variant={IncomingCallButtonVariants.ParkAndAccept}
              onClick={noop}
              onClickParkingLot={handleParkCallButtonClick}
              parkingLots={parkingLots}
            >
              {t('incomingCallModal.parkAndAccept')}
            </ParkCallButton>
          </Flex>
        )}
      </Flex>
    </MangoModal>
  )
}
